'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import Controllable from '../decorators/Controllable';
import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import devLogger from 'react-utils/devLogger';
import UICopyButton from '../button/UICopyButton';
import UIExpandingTextArea from '../input/UIExpandingTextArea';
import UITextArea from '../input/UITextArea';
import ShareInput from '../decorators/ShareInput';
import refObject from '../utils/propTypes/refObject';
import createChainablePropType from '../utils/propTypes/createChainablePropType';

const DefaultButtonRow = ({
  className,
  onClick,
  tooltipPlacement,
  use,
  value
}) => /*#__PURE__*/_jsx("div", {
  className: className,
  children: /*#__PURE__*/_jsx(UICopyButton, {
    onClick: onClick,
    tooltipPlacement: tooltipPlacement,
    use: use,
    value: value
  })
});

const getResizeOffset = evt => {
  const rect = evt.target.getBoundingClientRect();
  return {
    bottom: rect.bottom - evt.clientY,
    right: rect.right - evt.clientX
  };
};

class UICopyTextArea extends PureComponent {
  constructor(props) {
    super(props);

    this.handleCopyClick = evt => {
      const {
        inputRef,
        onCopy
      } = this.props;
      if (inputRef.current) inputRef.current.select();
      if (onCopy) onCopy(evt);
    };

    this.handleTextAreaMouseMove = evt => {
      const resizeHandleLength = 18;
      const {
        bottom,
        right
      } = getResizeOffset(evt);
      const isResizing = bottom < resizeHandleLength && right < resizeHandleLength;
      this.setState({
        isResizing
      });
    };

    this.handleTextAreaMouseDown = evt => {
      const {
        autoSelect,
        inputRef
      } = this.props;

      if (autoSelect && !this.state.isResizing) {
        evt.preventDefault();
        if (inputRef.current) inputRef.current.select();
      }
    };

    this.state = {
      isResizing: false
    };
  }

  render() {
    const _this$props = this.props,
          {
      autoSelect,
      buttonUse,
      buttonRowRenderer: ButtonRowRenderer,
      inputRef,
      onChange,
      textAreaProps,
      tooltipPlacement,
      use,
      value
    } = _this$props,
          rest = _objectWithoutPropertiesLoose(_this$props, ["autoSelect", "buttonUse", "buttonRowRenderer", "inputRef", "onChange", "onCopy", "textAreaProps", "tooltipPlacement", "use", "value"]);

    const isCode = use === 'code';
    const TextArea = isCode ? UITextArea : UIExpandingTextArea;

    if (use === 'code' && textAreaProps.maxHeight) {
      delete textAreaProps.maxHeight;
    }

    return /*#__PURE__*/_jsxs("div", Object.assign({}, rest, {
      children: [/*#__PURE__*/_jsx(TextArea, Object.assign({}, textAreaProps, {
        className: 'private-text-area--copyable' + (isCode ? " private-text-area--code is--code" : ""),
        inputRef: inputRef,
        onChange: onChange,
        onMouseMove: // Reconcile autoSelect and resize events when a maxHeight is set on expanding textareas
        !isCode && autoSelect && textAreaProps.resize !== false ? this.handleTextAreaMouseMove : null,
        onMouseDown: this.handleTextAreaMouseDown,
        readOnly: isCode ? true : textAreaProps.readOnly,
        resize: isCode ? false : textAreaProps.resize,
        value: value,
        $isCode: isCode
      })), /*#__PURE__*/_jsx(ButtonRowRenderer, {
        className: "m-y-3",
        onClick: this.handleCopyClick,
        tooltipPlacement: tooltipPlacement,
        use: buttonUse,
        value: value
      })]
    }));
  }

}

UICopyTextArea.propTypes = {
  autoSelect: PropTypes.bool.isRequired,
  buttonRowRenderer: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  buttonUse: UICopyButton.propTypes.use,
  inputRef: refObject,
  onChange: PropTypes.func,
  onCopy: PropTypes.func,
  textAreaProps: createChainablePropType((props, propName, componentName) => {
    if (typeof props[propName] !== 'object') {
      return new Error(`${componentName}\`: \`${propName}\` must be an object.`);
    }

    const {
      textAreaProps,
      use
    } = props;

    if (process.env.NODE_ENV !== 'production') {
      if (use === 'default' && textAreaProps.rows) {
        devLogger.warn({
          message: `\`${componentName}\`: Replace ${propName}.rows with ${propName}.maxHeight to limit height when \`use="${use}"\`.`,
          key: 'uicopytextarea-default-rows',
          url: 'https://tools.hubteamqa.com/ui-library/show/UIComponents/input/UICopyTextArea#uicopytextarea/limitingHeight'
        });
      } else if (use === 'code' && textAreaProps.maxHeight) {
        devLogger.warn({
          message: `\`${componentName}\`: Replace ${propName}.maxHeight with ${propName}.rows to limit height when \`use="${use}"\`.`,
          key: 'uicopytextarea-code-maxHeight',
          url: 'https://tools.hubteamqa.com/ui-library/show/UIComponents/input/UICopyTextArea#uicopytextarea/limitingHeight'
        });
      }
    }

    return null;
  }, 'object'),
  tooltipPlacement: UICopyButton.propTypes.tooltipPlacement,
  use: PropTypes.oneOf(['default', 'code']),
  value: PropTypes.string
};
UICopyTextArea.defaultProps = {
  autoSelect: true,
  buttonRowRenderer: DefaultButtonRow,
  buttonUse: 'tertiary',
  textAreaProps: {},
  tooltipPlacement: 'right',
  use: 'default'
};
UICopyTextArea.displayName = 'UICopyTextArea';
export default ShareInput(Controllable(UICopyTextArea));